import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createWeb3Modal, defaultConfig } from '@web3modal/ethers';
import { BrowserProvider, toBigInt } from 'ethers';

const WalletConnectContext = createContext(null);

export const WalletConnectProvider = ({ children }) => {
  const [address, setAddress] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);

  const projectId = '00337078baee9907cb654484724e03d4';
  const defaultNetwork = {
    chainId: 302,
    name: 'zkCandy Sepolia Testnet',
    currency: 'ETH',
    explorerUrl: 'https://sepolia.explorer.zkcandy.io/',
    rpcUrl: 'https://sepolia.rpc.zkcandy.io',
  };

  const metadata = {
    name: 'zkCandy Faucet',
    description: 'Faucet for zkCandy Sepolia Testnet',
    url: 'http://localhost:3000',
    icons: ['http://localhost:3000/favicon.ico'],
  };

  const web3Modal = createWeb3Modal({
    ethersConfig: defaultConfig({ metadata: metadata }),
    chains: [defaultNetwork],
    projectId: projectId,
  });

  const connect = async () => {
    try {
      setConnecting(true);
      await web3Modal.open();

      const web3Provider = await new Promise((resolve, reject) => {
        const interval = setInterval(() => {
          const provider = web3Modal.getWalletProvider();
          if (provider) {
            clearInterval(interval);
            resolve(provider);
          }
        }, 100);

        setTimeout(() => {
          clearInterval(interval);
          reject(new Error('Timed out waiting for wallet provider'));
        }, 60000);
      });

      if (!web3Provider) {
        throw new Error('No wallet provider found');
      }

      const ethersProvider = new BrowserProvider(web3Provider);
      const signer = await ethersProvider.getSigner();
      const address = await signer.getAddress();

      setAddress(address);
      setConnected(true);

      //web3Provider.on('chainChanged', handleChainChanged);
    } catch (error) {
      console.error('Could not connect to wallet', error);
      setConnected(false);
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    try {
      const web3Provider = web3Modal.getWalletProvider();
      if (web3Provider) {
        //web3Provider.removeListener('chainChanged', handleChainChanged);
      }
      await web3Modal.disconnect();
      setAddress('');
      setConnected(false);
    } catch (error) {
      console.error('Could not disconnect wallet', error);
    }
  };

  const switchNetwork = useCallback(async () => {
    const web3Provider = web3Modal.getWalletProvider();
    if (!web3Provider) throw new Error('WALLET_DISCONNECTED');

    const ethersProvider = new BrowserProvider(web3Provider);
    const currentNetwork = await ethersProvider.getNetwork();

    if (currentNetwork.chainId === toBigInt(defaultNetwork.chainId)) {
      console.log('Network match as default. Skip switch.');
      return;
    }

    try {
      await ethersProvider.send('wallet_switchEthereumChain', [{ chainId: '0x12E' }]);
    } catch (error) {
      console.error(error);
      await addNetwork();
      throw error;
    }
  }, [defaultNetwork.chainId]);

  const addNetwork = useCallback(async () => {
    const web3Provider = web3Modal.getWalletProvider();
    if (!web3Provider) throw new Error('WALLET_DISCONNECTED');

    const ethersProvider = new BrowserProvider(web3Provider);
    try {
      await ethersProvider.send('wallet_addEthereumChain', [{
        chainId: '0x12E',
        chainName: defaultNetwork.name,
        nativeCurrency: {
          name: defaultNetwork.name,
          symbol: defaultNetwork.currency,
          decimals: 18,
        },
        rpcUrls: [defaultNetwork.rpcUrl],
        blockExplorerUrls: [defaultNetwork.explorerUrl],
      }]);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }, [defaultNetwork]);

  useEffect(() => {
    let isSubscribed = true;

    const handleStateChange = (state) => {
      if (isSubscribed && connecting !== state.open) {
        setConnecting(state.open);
      }
    };

    const handleProviderChange = async (state) => {
      if (isSubscribed && (connected !== state.isConnected || address !== state.address)) {
        setConnected(state.isConnected);
        if (state.address) {
          const web3Provider = web3Modal.getWalletProvider();
          if (!web3Provider) {
            console.error('No wallet provider found');
            return;
          }
          const ethersProvider = new BrowserProvider(web3Provider);
          const signer = await ethersProvider.getSigner();

          setAddress(state.address);

          await switchNetwork();
        }
      }
    };

    const unsubscribeState = web3Modal.subscribeState(handleStateChange);
    const unsubscribeProvider = web3Modal.subscribeProvider(handleProviderChange);

    return () => {
      isSubscribed = false;
      unsubscribeState();
      unsubscribeProvider();
    };
  }, [connecting, connected, address, switchNetwork, web3Modal]);

  return (
    <WalletConnectContext.Provider value={{
      connect,
      disconnect,
      address,
      connected,
      connecting,
      switchNetwork,
      addNetwork,
    }}>
      {children}
    </WalletConnectContext.Provider>
  );
};

export const useWalletConnect = () => useContext(WalletConnectContext);
